import React, { CSSProperties } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';

import { WeeklyPlannerDateRangeOptions as RangeOptions } from '~constants/maps';

import { dateInputFieldFormat, formatDateString, weeklyPlannerDayDisplayFormat } from '~libs/dayjs';
import { LoadingState } from '~libs/reduxUtils';
import { Appointment, Client, HeaderCellProps, Worker } from '~weekly-planner/lib/common';

import Icon, { faBriefcaseClock, faCalendar } from '~components/icon';
import InfiniteScroll from '~components/InfiniteScroll';
import Spinner from '~components/Spinner';

import { useGetEventsQuery } from '~lookups/actions/events';

import { patchParams } from '~weekly-planner/reducers/weeklyPlanner';

import { selectDerivedWeekRange, selectParams } from '~weekly-planner/selectors';

import Row from './Row';

interface ComponentProps {
  headerCell: React.FC<HeaderCellProps>;
  headerTitle: string;
  rows: Array<Worker | Client>;
  focusId?: number | null;
  loadingState: LoadingState | null;
  getAppointmentTitle: (appointment: Appointment) => string;
  isDashboardView?: boolean;
}

const View: React.FC<ComponentProps> = ({
  headerCell: HeaderCell,
  headerTitle,
  rows,
  focusId,
  loadingState,
  getAppointmentTitle,
  isDashboardView = false,
}) => {
  const dispatch = useAppDispatch();
  const { date: dateParam } = useAppSelector(selectParams);
  const { limit } = useAppSelector(selectParams);

  const weekRange = useAppSelector(selectDerivedWeekRange);
  const { data: events } = useGetEventsQuery(weekRange ?? skipToken);

  const isLoading = loadingState === 'pending';
  const isLoaded = loadingState === 'fulfilled';
  const weekStart = dayjs(dateParam).weekday(0);
  const daysOfWeek = Array.from({ length: 7 }, (_, i) => dayjs(weekStart).add(i, 'day'));
  const date = dayjs(dateParam);
  const focusedRow = rows?.find(({ id }) => Number(id) === focusId);

  const setView = (selectedDate: Dayjs) => {
    const formattedDate = selectedDate.format(dateInputFieldFormat);
    dispatch(patchParams({ date: formattedDate, rangeType: RangeOptions.DAILY }));
  };

  const renderHeader = (days: Dayjs[]) => (
    <div className="header-wrapper">
      <div className="header-cell" title={headerTitle}>
        {headerTitle}
      </div>
      {days.map((day) => {
        const formattedDate = day.format(weeklyPlannerDayDisplayFormat);
        const publicHoliday = events?.find(({ start_date }) => formatDateString(start_date) === formatDateString(day));
        const label = (
          <>
            {publicHoliday && <Icon icon={faCalendar} className="me-1" title={publicHoliday.name} />}
            {formattedDate}
          </>
        );

        const className = classNames('header-cell', {
          today: day.isSame(dayjs(), 'day'),
          'public-holiday': !!publicHoliday,
        });

        return (
          <div key={day.toString()} className={className}>
            {!isDashboardView ? (
              <button className="clickable btn btn-link p-0" onClick={() => setView(day)} title={formattedDate}>
                {label}
              </button>
            ) : (
              <span className="non-clickable" title={formattedDate}>
                {label}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderFocusedRow = (
    days: Dayjs[],
    row: Worker | Client,
    getAppointmentTitle: (appointment: Appointment) => string,
  ) => (
    <div className="focus-row">
      {renderHeader(days)}
      <Row
        key={`${row.id}-0`}
        headerCell={<HeaderCell data={row} date={date} />}
        data={row}
        days={days}
        getAppointmentTitle={getAppointmentTitle}
        date={date}
      />
    </div>
  );

  const renderRows = (
    rows: Array<Worker | Client>,
    getAppointmentTitle: (appointment: Appointment) => string,
    focusedRow?: Worker | Client,
  ) => {
    const rowSet = rows
      ?.filter(({ id }) => focusedRow?.id !== id)
      .map((row, index) => {
        const gridRow = index + 1;

        const style: CSSProperties = {
          gridRow,
          gridColumn: 1,
        };
        return (
          <Row
            key={`${row.id}-${index}`}
            headerCell={<HeaderCell data={row} date={date} style={style} isDashboardView={isDashboardView} />}
            data={row}
            days={daysOfWeek}
            date={date}
            getAppointmentTitle={getAppointmentTitle}
            style={style}
            isDashboardView={isDashboardView}
          />
        );
      });

    return rowSet.length > 0 ? rowSet : null;
  };

  const loadMore = () => {
    if (!isLoading && limit === rows.length) {
      dispatch(patchParams({ limit: limit + 30 }));
    }
  };

  const emptyRows = isLoaded && rows.length === 0;

  const className = classNames('weekly-view-wrapper', {
    'focused-row': focusedRow,
  });

  const containerClassNames = classNames('weekly-view-container', {
    'initial-load': rows.length < 1,
  });

  return (
    <div className={className}>
      {!isDashboardView && (
        <>
          {focusedRow && renderFocusedRow(daysOfWeek, focusedRow, getAppointmentTitle)}
          {!focusedRow && renderHeader(daysOfWeek)}
          {!emptyRows ? (
            <InfiniteScroll
              dataLength={rows.length}
              className={containerClassNames}
              loadMore={loadMore}
              hasMore={true}
              loader={rows.length > 0 ? <Spinner loading={isLoading} /> : null}
              triggerOffset="500px"
              endMessage={
                <div className="empty-results">
                  <Icon icon={faBriefcaseClock} />
                  <p>End of results - Please adjust filters to find more.</p>
                </div>
              }
            >
              {renderRows(rows, getAppointmentTitle, focusedRow)}
              {isLoading && rows.length < 1 && <Spinner loading={isLoading} className="initial-load-spinner" />}
            </InfiniteScroll>
          ) : (
            <div className="empty-results">
              <Icon size="2x" className="mb-3" icon={faBriefcaseClock} />
              <p>No Results Found - Please adjust filters to find more.</p>
            </div>
          )}
        </>
      )}
      {isDashboardView && (
        <div className="weekly-view-container dashboard-container">
          {renderHeader(daysOfWeek)}
          {renderRows(rows, getAppointmentTitle)}
          {isLoading && <Spinner loading />}
        </div>
      )}
    </div>
  );
};

export default View;
